import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { Button } from "components/Forms/StyledComponents";
import SelectField from "components/Forms/SelectField";
import PageLoading from "components/PageLoading";
import { OptionGroup, StyledPaper, VerticalLine } from "components/Styled";
import PillButton from "components/Forms/PillButton";
import useSecurityDefinitions from "utils/useSecurityDefinitions";
import apiRequest from "utils/apiRequestWithErrorCode";
import { storageTypes, toStorageType } from "utils/storageProviders";
import useFormState from "utils/useFormState";
import Predicates from "./Predicates";
import DefaultDefinition from "./DefaultDefinition";
import { FormTouched, hasErrors, validate } from "./validation";
import { StyledTitle } from "../Styled";

import { FormState } from "../types";
import {
  RetentionConflictDetails,
  SecurityPolicy,
  StorageProvider,
} from "@dashboard-v3/api";
import StorageProviderLabel from "components/StorageProviderLabel";
import RetentionConflictModal, {
  getRetentionConflictDetails,
} from "components/RetentionConflictModal";

export default function Form() {
  const { t } = useTranslation("securityPolicies");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { state, pathname } = useLocation();
  const { id: paramId } = useParams();
  const form = useFormState<FormState>(formDefaults());
  const secureDefs = useSecurityDefinitions();
  const [loading, setLoading] = useState<boolean>(false);
  const [touched, setTouched] = useState<FormTouched>({});
  const [enableDefault, setEnableDefault] = useState<boolean>(false);
  // Reglas cuyos archivos duran menos que el link de esta policy (MXH-6086)
  const [retentionConflict, setRetentionConflict] =
    useState<RetentionConflictDetails>(null);
  const isEdit: boolean = pathname.includes("/edit");
  const errors = validate(form.formState, t, enableDefault);
  const { name, description, storageType, predicates, defaultDefinition } =
    form.formState;

  useEffect(() => {
    const handleEditData = async () => {
      if (state) {
        const { selectedPolicy, policyId } = state;
        return policyId !== paramId
          ? await fetchPolicyById(paramId)
          : form.setValues({
              ...selectedPolicy,
              storageType: toStorageType(selectedPolicy.storageType),
            });
      }
      await fetchPolicyById(paramId);
    };

    if (isEdit) {
      handleEditData();
      secureDefs.fetchDefinitions(1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEdit && defaultDefinition) setEnableDefault(true);
  }, [isEdit, defaultDefinition]);

  async function updateStorageType(event) {
    const newStorageType = event.target.value;
    form.updateValues(event);
    if (storageType !== newStorageType) {
      await secureDefs.fetchDefinitions(1000);
    }
  }

  const fetchPolicyById = async (id: string) => {
    setLoading(true);
    try {
      const res = await apiRequest<SecurityPolicy>(
        "GET",
        `/security-policies/${id}`
      );
      form.setValues({
        name: res.name,
        description: res.description,
        storageType: toStorageType(res.storageType),
        predicates: res.predicates,
        defaultDefinition: res.defaultDefinition,
      });
    } catch (e: unknown) {
      enqueueSnackbar(t("common:errors.fetchError"), {
        variant: "error",
      });
      history.push("/security-policies");
    } finally {
      setLoading(false);
    }
  };

  const getPredicateDefinitions = () => {
    const { formState } = form;

    return [
      ...secureDefs.getPresetsByProvider(formState.storageType),
      ...secureDefs.getByStorageType(formState.storageType),
    ];
  };

  const savePolicy = async (body: FormState, id?: string) => {
    setLoading(true);
    try {
      const method = id ? "PUT" : "POST";
      const path: string = id
        ? `/security-policies/${id}`
        : "/security-policies";
      await apiRequest<SecurityPolicy>(method, path, body);
      enqueueSnackbar(t("saveSuccess", { name: body.name }), {
        variant: "success",
      });
    } catch (error) {
      const conflictDetails = getRetentionConflictDetails(error);

      if (conflictDetails) {
        setRetentionConflict(conflictDetails);
        setLoading(false);
        return;
      }

      enqueueSnackbar(t("errors.saveError"), {
        variant: "error",
      });
    }

    setLoading(false);
    history.push("/security-policies");
  };

  const handleDefaultToggle = () => {
    const next = !enableDefault;
    setEnableDefault(next);
    if (!next) {
      form.reset(["defaultDefinition"]);
      setTouched(prev => ({ ...prev, defaultDefinition: false }));
    }
  };

  const markAllTouched = () => {
    setTouched({
      name: true,
      description: true,
      storageType: true,
      defaultDefinition: true,
      predicates: predicates.map(() => ({
        variableIdList: true,
        definitionId: true,
      })),
    });
  };

  const handleBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = e => {
    const fieldName = e.target.name;
    if (!fieldName) return;
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handlePredicateBlur =
    (index: number) => (field: "variableIdList" | "definitionId") => {
      setTouched(prev => {
        const next = [...(prev.predicates || [])];
        next[index] = { ...next[index], [field]: true };
        return { ...prev, predicates: next };
      });
    };

  const handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void = e => {
    e.preventDefault();

    if (hasErrors(errors)) {
      markAllTouched();
      return;
    }

    const { formState } = form;
    if (state && isEdit) {
      const { policyId } = state;
      savePolicy(formState, policyId);
    } else {
      savePolicy(formState);
    }
  };

  const addPredicate = () => {
    const { formState } = form;
    const {
      predicates: [predicate],
    } = formDefaults();

    form.setValues({
      predicates: [...formState.predicates, predicate],
    });

    setTouched(prev => ({
      ...prev,
      predicates: [...(prev.predicates || []), {}],
    }));
  };

  const removePredicate = (index: number) => {
    if (predicates.length <= 1) return;

    form.setValues({
      predicates: predicates.filter((_, i) => i !== index),
    });

    setTouched(prev => ({
      ...prev,
      predicates: (prev.predicates || []).filter((_, i) => i !== index),
    }));
  };

  return (
    <form onSubmit={handleSubmit}>
      <PageLoading loading={loading || secureDefs.loading} />
      <Grid container spacing={5}>
        <Grid item lg={8} xs={12}>
          <Paper style={{ padding: "24px 24px 32px 24px" }}>
            <Typography
              gutterBottom
              variant="h6"
              style={{ marginBottom: "1rem" }}
            >
              {t(`form.${isEdit ? "edit" : "new"}.title`)}
            </Typography>
            <OptionGroup>
              <TextField
                name="name"
                label={t("form.name.label")}
                variant="outlined"
                fullWidth
                value={name}
                onChange={form.updateValues}
                onBlur={handleBlur}
                error={Boolean(touched.name && errors.name)}
                helperText={(touched.name && errors.name) || ""}
                inputProps={{ "data-testid": "sp-name" }}
              />
            </OptionGroup>
            <OptionGroup>
              <TextField
                name="description"
                label={t("form.description.label")}
                variant="outlined"
                fullWidth
                value={description}
                onChange={form.updateValues}
                onBlur={handleBlur}
                error={Boolean(touched.description && errors.description)}
                helperText={(touched.description && errors.description) || ""}
                inputProps={{ "data-testid": "sp-description" }}
              />
            </OptionGroup>
            <OptionGroup>
              <StyledTitle>{t("form.storageType.title")}</StyledTitle>
              <SelectField
                name="storageType"
                label={t("form.storageType.label")}
                options={storageTypes}
                value={storageType}
                onChange={updateStorageType}
                onBlur={handleBlur}
                error={Boolean(touched.storageType && errors.storageType)}
                helperText={(touched.storageType && errors.storageType) || ""}
                data-testid="sp-storage-type"
                renderOption={(value: StorageProvider) => (
                  <MenuItem key={value} value={value}>
                    <StorageProviderLabel value={value} />
                  </MenuItem>
                )}
              />
            </OptionGroup>
          </Paper>
          {predicates.map((_, i) => (
            <Predicates
              key={`predicates-${i}`}
              index={i}
              definitions={getPredicateDefinitions()}
              errors={errors.predicates?.[i]}
              touched={touched.predicates?.[i]}
              onBlur={handlePredicateBlur(i)}
              onRemove={removePredicate}
              formData={{
                formState: form.formState,
                setValues: form.setValues,
              }}
            />
          ))}
          <PillButton
            wording={t("form.buttons.predicates")}
            onClick={addPredicate}
            lineSeparator="full"
          />
          <DefaultDefinition
            value={defaultDefinition}
            enableDefault={enableDefault}
            onToggle={handleDefaultToggle}
            onDefault={form.updateValues}
            onBlur={handleBlur}
            error={errors.defaultDefinition}
            touched={touched.defaultDefinition}
            definitions={secureDefs
              .getPresetsByProvider(form.formState.storageType)
              .concat(secureDefs.getByStorageType(form.formState.storageType))}
          />
          <VerticalLine size="sm" />
          <StyledPaper style={{ padding: "25px 24px" }}>
            <Button
              type="submit"
              size="large"
              wording={t("form.buttons.savePolicy")}
              fullWidth
            />
          </StyledPaper>
        </Grid>
      </Grid>
      {retentionConflict && (
        <RetentionConflictModal
          open
          origin="securityPolicy"
          details={retentionConflict}
          savingId={paramId}
          onClose={() => setRetentionConflict(null)}
        />
      )}
    </form>
  );
}

function formDefaults(): FormState {
  return {
    name: "",
    description: "",
    storageType: "" as StorageProvider,
    predicates: [
      {
        variableIdList: [],
        definitionId: "",
        match: true,
        firstMatch: false,
      },
    ],
    defaultDefinition: "",
  };
}
